<template>
  <div class="agent agent-page" v-if="agentData">
    <MetaTags v-if="metaInfo" :meta-info="metaInfo" />
    <div class="agent__introduction">
      <div class="agent__introduction__content">
        <div class="agent__introduction__image">
          <div
            class="agent-image"
            v-if="agentData?.pictureUrl"
            :style="`background-image: url(${agentData.pictureUrl})`"
          ></div>
          <div class="agent-image placeholder" v-if="!agentData?.pictureUrl">
            <i class="missing-icon icon-person-circled"></i>
          </div>

          <div class="agent-contact" :class="`agent-contact ${getAgentContactClassName()}`">
            <contact-info-box
              :phone="agentData?.phone"
              :mobile="agentData?.mobile"
              :email="agentData?.email"
              iconClass="icon-speech-bubble-circled"
            ></contact-info-box>
            <form-button text="Ota yhteyttä" theme="primary" @click.native="scrollTo('contact-form')"></form-button>
          </div>
        </div>

        <div class="agent__introduction__info">
          <h1 ref="agentName">{{ agentData.name }}</h1>

          <div class="highlighted-info">
            <div v-if="agentData.title">{{ agentData.title }}</div>
            <div v-if="getLanguageSkills()">Kielitaito: {{ getLanguageSkills() }}</div>
            <div v-if="agentData.degrees">Koulutus: {{ agentData.degrees }}</div>
          </div>

          <div class="agent-contact" :class="`agent-contact ${getAgentContactClassName()}`">
            <contact-info-box
              :phone="agentData.phone"
              :mobile="agentData.mobile"
              :email="agentData.email"
              iconClass="icon-speech-bubble-circled"
            ></contact-info-box>
            <form-button text="Ota yhteyttä" theme="primary" @click.native="scrollTo('contact-form')"></form-button>
          </div>

          <p v-for="experience in agentData.experience">
            <template v-if="isALink(experience)">
              <a :href="experience" class="basic-link" target="_blank">{{ experience }}</a>
            </template>
            <template v-else>{{ experience }}</template>
          </p>
          <p v-for="info in agentData.info">
            <template v-if="isALink(info)">
              <a :href="info" class="basic-link" target="_blank">{{ info }}</a>
            </template>
            <template v-else>{{ info }}</template>
          </p>

          <div class="agent__introduction__agency" v-if="agentData.company && agentData.office">
            <h2>
              <RouterLink class="basic-link" :to="`${agencies.path}/${getPostOfficeForUrl()}`">
                {{ getPostOfficeForLabel() }}
                <span>{{ agentData.company.name }}</span>
              </RouterLink>
            </h2>
            <template v-if="agentData.office.contact">
              <span>{{ agentData.office.contact.streetAddress }}</span>
              <span>{{ agentData.office.contact.postalCode }} {{ agentData.office.contact.postOffice }}</span>
            </template>
          </div>

          <div class="video" v-if="agentData.videoUrl">
            <iframe class="youtube" :src="agentData.videoUrl" frameborder="0" allowfullscreen></iframe>
          </div>
        </div>
      </div>
    </div>

    <div v-if="agentData.references && agentData.references.length" class="agent__references">
      <div class="agent__references__content">
        <h2>Asiakaspalautteet</h2>
        <div class="agent__references__carousel">
          <div class="scroller" :style="`left: ${referencePage * -100}%;`">
            <div class="reference" v-for="reference in agentData.references">
              {{ reference }}
            </div>
          </div>
        </div>
        <button
          v-if="pageCountOfReferences() > 1 && referencePage > 0"
          @click.prevent="moveInReferences(false)"
          class="navigate-button left"
          aria-label="Selaa palautteita"
        >
          <i class="icon-chevron-left"></i>
        </button>
        <button
          v-if="pageCountOfReferences() > 1 && referencePage < pageCountOfReferences() - 1"
          @click.prevent="moveInReferences(true)"
          class="navigate-button right"
          aria-label="Selaa palautteita"
        >
          <i class="icon-chevron-right"></i>
        </button>
      </div>
    </div>

    <div class="agent__listings" v-if="agentListings && agentListings.length">
      <div class="agent__listings__content">
        <suggested-listings title="Kysy minulta näistä kohteista" :suggestions="agentListings">
          <template #footnote>
            <RouterLink
              :to="{ path: apartments, query: { companyId: agentData.company.id } }"
              class="basic-link action-link"
            >
              Katso kaikki myytävät asuntomme
            </RouterLink>
          </template>
        </suggested-listings>
      </div>
    </div>
    <div class="agency__listings" v-else-if="agentCompanyListings && agentCompanyListings.length">
      <div class="agency__listings__content">
        <suggested-listings
          :title="`Uusimmat myytävät asunnot - ${agentData.company.name}`"
          :suggestions="agentCompanyListings"
        >
          <template #footnote>
            <RouterLink
              :to="{ path: apartments, query: { companyId: agentData.company.id } }"
              class="basic-link action-link"
            >
              Katso kaikki myytävät asuntomme
            </RouterLink>
          </template>
        </suggested-listings>
      </div>
    </div>

    <div class="contact-form-container" id="contact-form">
      <basic-contact
        request-type="agent"
        level="agent"
        :agent="agentData"
        :company="agentData.company"
        :office="agentData.office"
        :analytics="formAnalytics"
      ></basic-contact>
    </div>

    <dialogue identifier="single-agent-dialogue">
      <template #dialogueContent>
        <basic-contact
          request-type="agent"
          level="agent"
          :agent="agentData"
          :company="agentData.company"
          :office="agentData.office"
          :analytics="formAnalytics"
        >
        </basic-contact>
      </template>
    </dialogue>

    <spinner></spinner>
    <component :is="'script'" type="application/ld+json">
      {{ getSeoJSON }}
    </component>
  </div>
</template>

<script lang="ts">
import config from '@/config';
import MetaTags from '@/app/components/MetaTags.vue';
import * as actions from '@/app/store/action-types';
import ContactInfoBox from '@/app/components/contact-info-block/contact-info-block.vue';
import Result from '@/app/components/listing-result/listing-result.vue';
import utils from '@/app/utils/utils';
import BasicContact from '@/app/components/basic-contact/basic-contact.vue';
import { search, agencies } from '@/app/routes';
import VueScrollTo from 'vue-scrollto';
import { computed, defineAsyncComponent, defineComponent, type Ref, ref } from 'vue';
import Spinner from '@/app/components/spinner/spinner.vue';
import useAgentData from '@/app/data/useAgentData';
import useRecaptcha from '@/app/composables/useRecaptcha';
import useSafeRoute, { getRouteParamAsString } from '@/app/composables/useSafeRoute';
import { useListingsData } from '@/app/data/useListingsData';
import isSSR from '@/utils/isSSR';
import type { FiltersType } from '@/types/schemaTypes';
import { useFocusWithWatch } from '@/app/composables/useFocusWithWatch';

export default defineComponent({
  name: 'agent',
  setup() {
    useRecaptcha();
    const route = useSafeRoute();

    // Agent
    const id = getRouteParamAsString(route.value.params.agentId) || '';
    const { data: agentData, error } = useAgentData(id);
    const agentId = computed(() => agentData.value?.agentId);

    // Agent listings
    const agentFilters: Ref<FiltersType> = computed(() => ({
      agentId: agentData.value?.agentId,
    }));
    const enabled = computed(() => !!agentData.value && !isSSR && !!agentId.value);
    const { data: agentListingsData } = useListingsData(agentFilters, enabled);
    const agentListings = computed(() => agentListingsData.value?.listings);

    // Agency listings
    const companyId = computed(() => agentData.value?.company?.id?.toString());
    const agencyFilters = computed(() => ({
      companyId: computed(() => agentData.value?.company?.id?.toString()),
      featureGroup: 'apartment',
      limit: 16,
    }));
    const companyListingsEnabled = computed(() => enabled.value && !!companyId.value);
    const { data: companyListingsData } = useListingsData(agencyFilters, companyListingsEnabled);
    const agentCompanyListings = computed(() => companyListingsData?.value?.listings);

    const agentName = ref<HTMLElement | null>(null);

    useFocusWithWatch(agentData, agentName);

    return { agentData, agentListings, agentCompanyListings, error, route, agentName };
  },
  data: () => ({
    agencies,
    referencePage: 0,
    formAnalytics: {
      eVar21: 'kiinteistovalitys',
      eVar22: 'viesti-valittajalle',
    },
  }),
  methods: {
    urlHasAgentShortName() {
      return this.agentData?.shortName && this.route.path.indexOf(this.agentData.shortName) !== -1;
    },
    scrollTo(id: string) {
      const duration = 400;
      const options = {
        easing: 'ease-out',
        offset: -50,
        cancelable: true,
      };
      VueScrollTo.scrollTo(document.getElementById(id), duration, options);
    },
    openContactForm() {
      this.$bus.$emit('openDialogue', 'single-agent-dialogue');
    },
    getMetaDescription() {
      return (
        (this.agentData.experience && this.agentData.experience.join(' ')) ||
        (this.agentData.info && this.agentData.info.join(' ')) ||
        ''
      ).slice(0, 160);
    },
    getAgentContactClassName() {
      return this.agentData?.experience || this.agentData?.info ? 'show-under-picture' : '';
    },
    getLanguageSkills() {
      if (this.agentData.languageSkills && this.agentData.languageSkills.length) {
        return this.agentData.languageSkills
          .map((skill: any) => config.LANGUAGES[skill as keyof typeof config.LANGUAGES] || skill)
          .join(', ');
      }
      return '';
    },
    analyticsCall() {
      const agencyId = getRouteParamAsString(this.route.params.agencyId);
      const agentId = getRouteParamAsString(this.route.params.agentId);
      const pageNameForAnalytics = this.urlHasAgentShortName()
        ? `kiinteistonvalitys:${agencyId}:${agentId}`
        : `valittaja:${agentId}`;

      void this.$store.dispatch(actions.analytics.set, {
        pageName: pageNameForAnalytics,
        agencyName: this.getPostOfficeForLabel(),
        eVar17: this.metaInfo.title,
      });

      void this.$store.dispatch(actions.analytics.track, config.analytics.eventTypes.pageView);
    },
    pageCountOfReferences() {
      return Math.ceil(this.agentData.references.length / 4);
    },
    moveInReferences(forward: boolean) {
      if (forward) {
        this.referencePage++;
      } else {
        this.referencePage--;
      }
    },
    preOpenContactFormIfNecessary() {
      const contact = this.route.query.contact?.toString() || '';
      if (contact.toLowerCase() === 'true') {
        this.openContactForm();
      }
    },
    getPostOfficeForUrl() {
      return utils.urlFriendlyString(
        this.agentData.office.contact.postOffice || this.agentData.company.contact.postOffice
      );
    },
    getPostOfficeForLabel() {
      return utils.capitalizeMultiPartWords(this.agentData.office.contact.postOffice);
    },
    isALink(text: any) {
      return text && text.startsWith('https');
    },
  },
  mounted() {
    this.preOpenContactFormIfNecessary();
    utils.loadImages(document, false);
  },
  computed: {
    getSeoJSON() {
      return `{
        "@context":"http://schema.org",
        "@type":"Person",
        "name":"${this.agentData.name}",
        "jobTitle":"${this.agentData.title}",
        "image":"${this.agentData.hasOwnProperty('imageUrl') ? this.agentData.pictureUrl : ''}",
        "telephone":"${this.agentData.mobile}",
        "email": "${this.agentData.email}",
        "url":"https://op-koti.fi${this.route.fullPath}"
      }`;
    },
    metaInfo() {
      const links = [];
      if (this.agentData.office && this.agentData.office.shortName) {
        links.push({
          rel: 'canonical',
          href: `${config.FRONT.PROD_BASE_URL}${agencies.path}/${this.agentData.office.shortName}/${this.agentData.shortName}`,
        });
      }
      return {
        title:
          `${this.getAgentTitle} ${this.agentData.name} - OP Koti` || 'Kiinteistönvälittäjää ei löytynyt - OP Koti',
        meta: [{ vmid: 'description', name: 'description', content: this.getMetaDescription() }],
        link: links,
      };
    },
    apartments() {
      return search.sale.routes.find((route) => route.id === config.testing.NAVIGATION.BUYING.APARTMENTS)?.path;
    },
    getAgentTitle() {
      return this.agentData.hasOwnProperty('title') ? this.agentData.title : '';
    },
  },
  watch: {
    agentData() {
      this.analyticsCall();
    },
  },
  components: {
    MetaTags,
    BasicContact,
    Dialogue: defineAsyncComponent(() => import('@/app/components/dialogue/dialogue.vue')),
    SuggestedListings: defineAsyncComponent(() => import('@/app/components/suggested-listings/suggested-listings.vue')),
    Result,
    FormButton: defineAsyncComponent(() => import('@/app/components/form-button/form-button.vue')),
    ContactInfoBox,
    Spinner,
  },
});
</script>
